import { useEffect, useState } from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { getIcoRegistration } from "../../data/request";
import { getDateDisplay } from "../../data/utilities";
import { IcoRegistrationResponse } from "../../types/IcoRegistration";
import { DELTA_PRIMARY, STATUS_GREEN, TEXT_SECONDARY } from "../colors";

// PRO: whether a company is registered as a data controller with the ICO
// (UK data protection regulator) and, if so, its fee tier - derived by
// DeltaGain from the ICO's own Register of Fee Payers, matched onto a
// company purely by name (there is no Companies House company number
// anywhere in that source data - see lambda_load_ico_register.py). Most
// companies genuinely have no match here, which is normal, not a failure -
// this renders a plain, non-alarming message rather than an error state.
export const IcoRegistrationPanel = (props: { companyNo: string | undefined }) => {
  const { companyNo } = props;
  const [response, setResponse] = useState<IcoRegistrationResponse>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (companyNo == undefined) {
        return;
      }
      setLoading(true);
      const doc = await getIcoRegistration(companyNo);
      setResponse(doc);
      setLoading(false);
    };

    fetchData();
  }, [companyNo]);

  if (loading) {
    return (
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 200,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!response?.registered) {
    return (
      <Box style={{ padding: 20 }}>
        <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
          No confident match found for this company on the ICO's Register of Fee Payers. Most UK
          companies are not required to register (e.g. if they process no personal data beyond
          standard staff admin), and some registrations simply can't be matched to a company by
          name alone - this doesn't necessarily mean the company isn't registered.
        </Typography>
      </Box>
    );
  }

  return (
    <Box style={{ padding: 20 }}>
      <Box style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <Chip
          label="Registered with the ICO"
          size="small"
          style={{ backgroundColor: STATUS_GREEN, color: "white" }}
        />
        {response.public_authority && (
          <Chip label="Public Authority" size="small" variant="outlined" />
        )}
      </Box>

      <Box style={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 10, columnGap: 24, maxWidth: 560 }}>
        <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>Registration number</Typography>
        <Typography variant="body2" style={{ color: DELTA_PRIMARY, fontWeight: 600 }}>{response.registration_number}</Typography>

        <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>Payment tier</Typography>
        <Typography variant="body2">{response.payment_tier}</Typography>

        <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>Registration start date</Typography>
        <Typography variant="body2">{getDateDisplay(response.start_date_of_registration)}</Typography>

        {response.end_date_of_registration && (
          <>
            <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>Registration end date</Typography>
            <Typography variant="body2">{getDateDisplay(response.end_date_of_registration)}</Typography>
          </>
        )}

        {response.trading_names && response.trading_names.length > 0 && (
          <>
            <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>Trading names</Typography>
            <Typography variant="body2">{response.trading_names.join(", ")}</Typography>
          </>
        )}
      </Box>

      {response.public_register_entry_url && (
        <Box style={{ marginTop: 20 }}>
          <a href={response.public_register_entry_url} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
            View this entry on the ICO's public register
          </a>
        </Box>
      )}
    </Box>
  );
};

export default IcoRegistrationPanel;
